import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import { StatCard } from '../components/StatCard'
import {
  Loader2,
  ArrowLeft,
  Edit,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  ShoppingBag,
  DollarSign,
  Percent,
} from 'lucide-react'
import { apiGet, formatMXN } from '../lib/api'

interface ClienteDetalleData {
  id: string
  nombre: string
  email: string
  telefono: string
  docType: string
  docNumero: string
  descuento: number
  direccion: string
  ciudad: string
  provincia: string
  pais: string
  cp: string
  activo: boolean
  role: string
  creadoEn: string
  stats: { totalPedidos: number; totalGastado: number }
  ultimosPedidos: Array<{
    id: string
    nrodePedido: string
    total: number
    estado: string
    fp: string
    paymentStatus: string
    fecha: string
  }>
}

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low.includes('ship') || low.includes('envia')) return <Badge variant="info">{s}</Badge>
  if (low.includes('deliver') || low.includes('entrega'))
    return <Badge variant="success">{s}</Badge>
  if (low.includes('cancel') || low.includes('refund'))
    return <Badge variant="danger">{s}</Badge>
  if (low.includes('prepar') || low.includes('paid')) return <Badge variant="warning">{s}</Badge>
  return <Badge>{s || 'Sin estado'}</Badge>
}

export function ClienteDetalle() {
  const { id } = useParams({ strict: false }) as { id: string }
  const { data, isLoading, error } = useQuery<ClienteDetalleData>({
    queryKey: ['cliente', id],
    queryFn: () => apiGet(`/api/admin/clientes/${id}`),
  })

  return (
    <>
      <Topbar
        title={data?.nombre || 'Cliente'}
        subtitle={data?.email}
        actions={
          <div className="flex gap-2">
            <Link
              to="/clientes"
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Volver
            </Link>
            {data && (
              <Link
                to="/clientes/$id/editar"
                params={{ id: data.id }}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
              >
                <Edit className="size-3.5" /> Editar
              </Link>
            )}
          </div>
        }
      />

      <div className="p-6">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
            <p className="mt-3 text-sm">Cargando cliente…</p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                label="Total pedidos"
                value={data.stats.totalPedidos.toLocaleString('es-MX')}
                icon={ShoppingBag}
                color="red"
              />
              <StatCard
                label="Total gastado"
                value={formatMXN(data.stats.totalGastado)}
                icon={DollarSign}
                color="emerald"
              />
              <StatCard
                label="Descuento"
                value={`${data.descuento}%`}
                icon={Percent}
                color="violet"
              />
              <StatCard
                label="Estado"
                value={data.activo ? 'Activo' : 'Inactivo'}
                icon={CreditCard}
                color={data.activo ? 'blue' : 'amber'}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-1">
                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Información general</h2>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">Nombre</dt>
                      <dd className="font-medium text-ink-900">{data.nombre || '—'}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">Rol</dt>
                      <dd>
                        <Badge variant={data.role === 'admin' ? 'danger' : 'default'}>
                          {data.role}
                        </Badge>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">
                        Documento
                      </dt>
                      <dd className="font-mono text-xs text-ink-700">
                        {data.docType || ''} {data.docNumero || '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-ink-500">Registro</dt>
                      <dd className="text-xs text-ink-700">
                        {new Date(data.creadoEn).toLocaleDateString('es-MX')}
                      </dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Contacto</h2>
                  <dl className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <Mail className="mt-0.5 size-4 text-ink-500" />
                      <div>
                        <dt className="text-xs uppercase tracking-wider text-ink-500">Email</dt>
                        <dd className="text-ink-900">{data.email}</dd>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      <Phone className="mt-0.5 size-4 text-ink-500" />
                      <div>
                        <dt className="text-xs uppercase tracking-wider text-ink-500">
                          Teléfono
                        </dt>
                        <dd className="text-ink-900">{data.telefono || '—'}</dd>
                      </div>
                    </div>
                  </dl>
                </div>

                <div className="rounded-2xl border border-ink-200 bg-white p-5">
                  <h2 className="mb-4 font-semibold text-ink-900">Dirección</h2>
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="mt-0.5 size-4 text-ink-500" />
                    <div className="text-ink-700">
                      {data.direccion ? (
                        <>
                          <div>{data.direccion}</div>
                          <div>
                            {data.ciudad}
                            {data.provincia ? `, ${data.provincia}` : ''}
                          </div>
                          <div className="text-xs text-ink-500">
                            {data.cp} · {data.pais}
                          </div>
                        </>
                      ) : (
                        <span className="text-ink-500">Sin dirección registrada</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
                  <div className="border-b border-ink-200 px-5 py-4">
                    <h2 className="font-semibold text-ink-900">Últimos pedidos</h2>
                    <p className="text-xs text-ink-500">Los 10 más recientes</p>
                  </div>
                  {data.ultimosPedidos.length === 0 ? (
                    <div className="grid place-items-center py-12 text-sm text-ink-500">
                      Este cliente no tiene pedidos.
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-ink-50">
                        <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                          <th className="px-5 py-3 font-medium">Pedido</th>
                          <th className="px-5 py-3 font-medium">Fecha</th>
                          <th className="px-5 py-3 font-medium text-right">Total</th>
                          <th className="px-5 py-3 font-medium">Pago</th>
                          <th className="px-5 py-3 font-medium">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.ultimosPedidos.map((p) => (
                          <tr
                            key={p.id}
                            className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                          >
                            <td className="px-5 py-3 font-mono text-xs font-medium text-ink-900">
                              #{p.nrodePedido}
                            </td>
                            <td className="px-5 py-3 text-ink-700">{p.fecha}</td>
                            <td className="px-5 py-3 text-right font-semibold text-ink-900">
                              {formatMXN(p.total)}
                            </td>
                            <td className="px-5 py-3 text-xs capitalize text-ink-700">{p.fp}</td>
                            <td className="px-5 py-3">{statusBadge(p.estado)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}
